import { useRef, useState, useSyncExternalStore } from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DotsHorizontalIcon,
  ImageIcon,
  PlusIcon,
  DragHandleDots2Icon,
} from '@radix-ui/react-icons';
import type { EbookWorkspace } from '@wx-weiqi/contracts';
import type { EbookClient } from '../services/ebooks';
import { isEbookQuestionReady } from '../services/ebook-editing';
import { ebookChapterNames, normalizeEbookChapter } from '../services/ebook-chapters';
import { EbookShelfCover } from './EbookShelfCover';
import { EbookThumbnail } from './EbookReader';
import { BoardSurface } from './BoardSurface';
import './ebook-library-arrange.css';

const COMPACT_HEIGHT = '(max-height: 800px)';
const subscribeHeight = (listener: () => void) => {
  const media = window.matchMedia(COMPACT_HEIGHT);
  media.addEventListener('change', listener);
  return () => media.removeEventListener('change', listener);
};
const compactHeight = () => window.matchMedia(COMPACT_HEIGHT).matches;
const serverCompactHeight = () => false;

export function EbookArrangePanel({
  client,
  documentId,
  data,
  busy,
  dirty,
  onChange,
  onUpload,
  onPreview,
  onEdit,
  onPublish,
  onSave,
  onLibrary,
}: {
  client: EbookClient;
  documentId: string;
  data: EbookWorkspace;
  busy: boolean;
  dirty: boolean;
  onChange: (data: EbookWorkspace) => void;
  onUpload: (file: File) => void;
  onPreview: (questionId?: string) => void;
  onEdit: (id: string) => void;
  onPublish: () => void;
  onSave: () => Promise<void>;
  onLibrary: () => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [previewTab, setPreviewTab] = useState<'directory' | 'question'>('question');
  // Keep the same question in view when resizing changes the number of visible rows.
  const [firstVisible, setFirstVisible] = useState(0);
  const pageSize = useSyncExternalStore(subscribeHeight, compactHeight, serverCompactHeight)
    ? 4
    : 6;
  const book = data.book;
  const byId = new Map(data.questions.map((q) => [q.id, q]));
  const ordered = book.questionIds.flatMap((id) => {
    const q = byId.get(id);
    return q ? [q] : [];
  });
  const previewQuestion = ordered.find((q) => q.id === selected) ?? ordered[0];
  const chapters = ebookChapterNames(book, data.questions);
  const chapterGroups = [
    ...chapters,
    ...(ordered.some((q) => !normalizeEbookChapter(q.chapter)) ? [''] : []),
  ];
  const pageCount = Math.max(1, Math.ceil(book.questionIds.length / pageSize));
  const currentPage = Math.min(Math.floor(firstVisible / pageSize), pageCount - 1);
  const incompleteCount = book.questionIds.filter((id) => {
    const q = byId.get(id);
    return !q || !isEbookQuestionReady(q);
  }).length;
  const update = (patch: Partial<EbookWorkspace['book']>) =>
    onChange({ ...data, book: { ...book, ...patch } });
  const move = (id: string, position: number) => {
    if (busy) return;
    const ids = book.questionIds.filter((item) => item !== id);
    const target = Math.max(0, Math.min(position, ids.length));
    ids.splice(target, 0, id);
    update({ questionIds: ids });
    setFirstVisible(target);
    setSelected(id);
  };
  const selectQuestion = (id: string) => {
    setSelected(id);
    setPreviewTab('question');
    setFirstVisible(book.questionIds.indexOf(id));
  };
  return (
    <section className="ebook-arrangement" aria-label="编排与预览" aria-busy={busy}>
      <header className="ebook-arrangement-heading">
        <h2>整理顺序，检查学生看到的内容</h2>
        <p>
          已编排 {book.questionIds.length} 题
          {incompleteCount > 0 ? ` · ${incompleteCount} 题待完善` : ''}
        </p>
      </header>
      <div className="ebook-arrangement-columns">
        <section className="ebook-arrangement-settings" aria-label="教材设置">
          <h3>教材设置</h3>
          <div className="ebook-arrangement-cover">
            <EbookShelfCover
              client={client}
              title={book.title}
              path={book.coverImage ? `/documents/${documentId}/covers/${book.coverImage}` : null}
            />
            <input
              ref={fileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label="选择封面图片"
              hidden
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onUpload(file);
              }}
            />
            <button type="button" disabled={busy} onClick={() => fileInput.current?.click()}>
              <ImageIcon aria-hidden="true" />
              {book.coverImage ? '更换封面' : '上传封面'}
            </button>
          </div>
          <label>
            书名
            <input
              value={book.title}
              maxLength={200}
              disabled={busy}
              onChange={(e) => update({ title: e.target.value })}
            />
          </label>
          <label>
            教材简介
            <textarea
              rows={3}
              value={book.introduction}
              maxLength={8000}
              disabled={busy}
              placeholder="练习目标或给学生的说明"
              onChange={(e) => update({ introduction: e.target.value })}
            />
          </label>
          <details className="ebook-arrangement-layout-settings">
            <summary>版式设置</summary>
            <label>
              封面题签
              <input
                value={book.cover}
                maxLength={200}
                disabled={busy}
                onChange={(e) => update({ cover: e.target.value })}
              />
            </label>
            <label>
              每屏密度
              <select
                value={book.density}
                disabled={busy}
                onChange={(e) => update({ density: Number(e.target.value) as 1 | 2 | 3 })}
              >
                {[1, 2, 3].map((n) => (
                  <option value={n} key={n}>
                    {n} 列（手机单列）
                  </option>
                ))}
              </select>
            </label>
            <label>
              基础字号
              <select
                value={book.fontSize}
                disabled={busy}
                onChange={(e) => update({ fontSize: Number(e.target.value) })}
              >
                {Array.from({ length: 11 }, (_, index) => index + 14).map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <label>
              题号样式
              <select
                value={book.numberStyle}
                disabled={busy}
                onChange={(e) => update({ numberStyle: e.target.value as 'SOURCE' | 'SEQUENTIAL' })}
              >
                <option value="SEQUENTIAL">顺序题号</option>
                <option value="SOURCE">资料原题号</option>
              </select>
            </label>
            <p>封面支持 JPG、PNG、WebP，最大 8 MB，建议 3:4 竖图。</p>
            {book.coverImage && (
              <button type="button" disabled={busy} onClick={() => update({ coverImage: null })}>
                移除封面图片
              </button>
            )}
          </details>
        </section>
        <section className="ebook-arrangement-order" aria-label="题目顺序">
          <header>
            <h3>题目顺序</h3>
            <button type="button" disabled={busy} onClick={onLibrary}>
              <PlusIcon aria-hidden="true" />
              从题库添加
            </button>
          </header>
          <div className="ebook-arrangement-order-list">
            {!book.questionIds.length && (
              <div className="ebook-curation-empty">
                <h3>先加入准备好的题目</h3>
                <p>在教材题库中选择已就绪题目，加入后即可排序和预览。</p>
                <button type="button" disabled={busy} onClick={onLibrary}>
                  前往教材题库
                </button>
              </div>
            )}
            {book.questionIds
              .slice(currentPage * pageSize, (currentPage + 1) * pageSize)
              .map((id, offset) => {
                const index = currentPage * pageSize + offset;
                const q = byId.get(id);
                if (!q)
                  return (
                    <div className="ebook-arrangement-missing" key={id}>
                      <span>第 {index + 1} 题内容缺失</span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          update({ questionIds: book.questionIds.filter((item) => item !== id) })
                        }
                      >
                        移出编排
                      </button>
                    </div>
                  );
                const chapter = normalizeEbookChapter(q.chapter);
                const previous = byId.get(book.questionIds[index - 1] ?? '');
                const showChapter =
                  offset === 0 || normalizeEbookChapter(previous?.chapter) !== chapter;
                const isSelected = previewQuestion?.id === id;
                return (
                  <div className="ebook-arrangement-order-entry" key={id}>
                    {showChapter && (
                      <h4>
                        {chapter || '未分章'}
                        <span>
                          {
                            ordered.filter(
                              (item) => normalizeEbookChapter(item.chapter) === chapter,
                            ).length
                          }{' '}
                          题
                        </span>
                      </h4>
                    )}
                    <div
                      className={`ebook-arrangement-row${isSelected ? ' is-selected' : ''}`}
                      draggable={!busy}
                      onDragStart={() => setDrag(id)}
                      onDragEnd={() => setDrag(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => {
                        if (drag && drag !== id) move(drag, index);
                        setDrag(null);
                      }}
                    >
                      <DragHandleDots2Icon className="ebook-arrangement-drag" aria-hidden="true" />
                      <button
                        type="button"
                        className="ebook-arrangement-select"
                        aria-pressed={isSelected}
                        onClick={() => selectQuestion(id)}
                      >
                        <EbookThumbnail question={q} illustrated />
                        <span className="ebook-arrangement-number">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="ebook-arrangement-row-copy">
                          <strong>{q.number ? `第 ${q.number} 题` : q.title}</strong>
                          <small>{q.goal || '题目要求待填写'}</small>
                          {!isEbookQuestionReady(q) && <em>待完善</em>}
                        </span>
                      </button>
                      <div className="ebook-arrangement-row-actions">
                        <button
                          type="button"
                          aria-label={`上移第 ${index + 1} 题`}
                          disabled={busy || index === 0}
                          onClick={() => move(id, index - 1)}
                        >
                          <ArrowUpIcon />
                        </button>
                        <button
                          type="button"
                          aria-label={`下移第 ${index + 1} 题`}
                          disabled={busy || index === book.questionIds.length - 1}
                          onClick={() => move(id, index + 1)}
                        >
                          <ArrowDownIcon />
                        </button>
                        <details className="ebook-curation-menu">
                          <summary aria-label={`第 ${index + 1} 题更多操作`}>
                            <DotsHorizontalIcon />
                          </summary>
                          <div>
                            <button type="button" disabled={busy} onClick={() => onEdit(id)}>
                              编辑题目
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() =>
                                update({
                                  questionIds: book.questionIds.filter((item) => item !== id),
                                })
                              }
                            >
                              移出编排
                            </button>
                          </div>
                        </details>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <nav className="ebook-curation-pagination" aria-label="编排题目分页">
            <button
              type="button"
              aria-label="上一页编排"
              disabled={currentPage === 0}
              onClick={() => setFirstVisible((currentPage - 1) * pageSize)}
            >
              <ChevronLeftIcon />
            </button>
            <span>
              第 {currentPage + 1} / {pageCount} 页
            </span>
            <button
              type="button"
              aria-label="下一页编排"
              disabled={currentPage + 1 === pageCount}
              onClick={() => setFirstVisible((currentPage + 1) * pageSize)}
            >
              <ChevronRightIcon />
            </button>
            <span>共 {book.questionIds.length} 题</span>
          </nav>
        </section>
        <section className="ebook-arrangement-preview" aria-label="学生视角预览">
          <header>
            <h3>学生视角预览</h3>
            <div className="ebook-arrangement-tabs" role="group" aria-label="预览内容">
              <button
                type="button"
                aria-pressed={previewTab === 'directory'}
                onClick={() => setPreviewTab('directory')}
              >
                目录
              </button>
              <button
                type="button"
                aria-pressed={previewTab === 'question'}
                onClick={() => setPreviewTab('question')}
              >
                题目
              </button>
            </div>
          </header>
          {previewTab === 'directory' ? (
            <div className="ebook-arrangement-directory">
              <h4>{book.title || '未命名教材'}</h4>
              {chapterGroups.map((name) => (
                <section key={name}>
                  <h5>{name || '未分章'}</h5>
                  {ordered
                    .filter((q) => normalizeEbookChapter(q.chapter) === name)
                    .map((q) => (
                      <button type="button" key={q.id} onClick={() => selectQuestion(q.id)}>
                        <span>
                          第{' '}
                          {book.numberStyle === 'SOURCE'
                            ? q.number
                            : book.questionIds.indexOf(q.id) + 1}{' '}
                          题
                        </span>
                        <span>{q.goal}</span>
                        <ChevronRightIcon aria-hidden="true" />
                      </button>
                    ))}
                </section>
              ))}
              {!ordered.length && <p>加入题目后，这里会显示学生看到的章节目录。</p>}
            </div>
          ) : previewQuestion ? (
            <div className="ebook-arrangement-question-preview">
              <div className="ebook-arrangement-preview-copy">
                <h4>
                  第{' '}
                  {book.numberStyle === 'SOURCE'
                    ? previewQuestion.number
                    : book.questionIds.indexOf(previewQuestion.id) + 1}{' '}
                  题
                </h4>
                <p>
                  {previewQuestion.currentPlayer === 'BLACK' ? '黑先' : '白先'}
                  {previewQuestion.goal ? `，${previewQuestion.goal}` : ''}
                </p>
              </div>
              <figure className="ebook-arrangement-board" aria-label="学生可见的题目初始棋形">
                <BoardSurface
                  boardAppearance="wood"
                  stoneAppearance="shell"
                  size={previewQuestion.size}
                  stones={previewQuestion.stones}
                  lastMove={null}
                  initialAnnotations={previewQuestion.marks.map((mark) =>
                    mark.kind === 'LABEL'
                      ? { point: mark.point, kind: 'LABEL' as const, text: mark.label }
                      : { point: mark.point, kind: mark.kind },
                  )}
                />
              </figure>
              <button
                type="button"
                className="primary ebook-arrangement-practice"
                disabled={busy || !isEbookQuestionReady(previewQuestion)}
                onClick={() => onPreview(previewQuestion.id)}
              >
                开始练习
              </button>
              <p className="ebook-arrangement-preview-note">教师试做，不计入学生学习记录。</p>
            </div>
          ) : (
            <div className="ebook-curation-empty">
              <p>从题库加入题目后，可在这里检查初始棋形。</p>
            </div>
          )}
        </section>
      </div>
      <footer className="ebook-curation-footer">
        <p className={dirty ? 'ebook-arrangement-unsaved' : ''}>
          {dirty ? '有未保存修改' : '修改已保存'} · 发布后学生才可看到更新
        </p>
        <div className="ebook-curation-footer-actions">
          <button type="button" disabled={busy || !dirty} onClick={() => void onSave()}>
            保存编排
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || !book.questionIds.length}
            onClick={onPublish}
          >
            下一步：发布
            <ChevronRightIcon aria-hidden="true" />
          </button>
        </div>
      </footer>
    </section>
  );
}
